import { useEffect, useRef, useState, useCallback } from "react";
import { CircularProgress, Box, Button, Typography } from "@mui/material";

const TERMLY_POLICY_ID = "e220009e-ceee-49d9-aead-5c0e4fd8f19e";
const SCRIPT_ID = "termly-jssdk";
const MAX_RETRIES = 3;
const CONTENT_CHECK_INTERVAL = 300;
const CONTENT_CHECK_TIMEOUT = 10000;

const PrivacyPolicy = () => {
  const embedRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [retryCount, setRetryCount] = useState(0);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const activeRef = useRef(true);

  const stopPolling = useCallback(() => {
    clearInterval(intervalRef.current);
    clearTimeout(timeoutRef.current);
  }, []);

  const waitForContent = useCallback(() => {
    intervalRef.current = setInterval(() => {
      if (!activeRef.current) return;
      if (embedRef.current?.children.length > 0) {
        stopPolling();
        setStatus("success");
      }
    }, CONTENT_CHECK_INTERVAL);

    timeoutRef.current = setTimeout(() => {
      if (!activeRef.current) return;
      stopPolling();
      setStatus("error");
    }, CONTENT_CHECK_TIMEOUT);
  }, [stopPolling]);

  const loadTermly = useCallback(() => {
    stopPolling();
    activeRef.current = true;
    setStatus("loading");

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();

    if (embedRef.current) embedRef.current.innerHTML = "";

    const timer = setTimeout(() => {
      if (!activeRef.current) return;

      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://app.termly.io/embed-policy.min.js";
      script.async = true;
      script.onload = () => {
        if (activeRef.current) waitForContent();
      };
      script.onerror = () => {
        if (activeRef.current) setStatus("error");
      };

      document.body.appendChild(script);
    }, 50);

    return () => clearTimeout(timer);
  }, [stopPolling, waitForContent]);

  useEffect(() => {
    window.scrollTo(0, 0);
    activeRef.current = true;
    const cancel = loadTermly();
    return () => {
      activeRef.current = false;
      stopPolling();
      if (cancel) cancel();
    };
  }, [loadTermly, stopPolling]);

  const handleRetry = () => {
    if (retryCount < MAX_RETRIES) {
      setRetryCount((c) => c + 1);
      loadTermly();
    }
  };

  return (
    <div style={{ padding: "40px", maxWidth: "960px", margin: "0 auto" }}>
      {status === "loading" && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {status === "error" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            minHeight: "400px",
            justifyContent: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Failed to load the Privacy Policy.
          </Typography>
          {retryCount < MAX_RETRIES ? (
            <Button variant="outlined" onClick={handleRetry}>
              Retry ({MAX_RETRIES - retryCount} attempts left)
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Please refresh the page or try again later.
            </Typography>
          )}
        </Box>
      )}
      <div
        ref={embedRef}
        name="termly-embed"
        data-id={TERMLY_POLICY_ID}
        data-type="iframe"
        style={{ display: status === "success" ? "block" : "none" }}
      />
    </div>
  );
};

export default PrivacyPolicy;
